import { useQuery, useMutation, useLazyQuery } from "@apollo/client/react";
import { Grid } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useVisita } from "../../../../../../context/VisitaContext";
import SAVE_PROGRAMA_CEAE_MEDICO, { adaptAddVisitaCEAEMedicoInput } from "../../../../../../data/graphQL/mutations/Visitas/saveProgramaCEAEMedico";
import { useStepSave } from "../../../../hooks/useStepSave";
import CardBox from "../../CardBox/CardBox";
import { filterVisitaData, getInputNames } from "../../../../utils/formDataHelper";
import { getStringQueryCombosRI } from "../../../../../../data/graphQL/queries/ResumenIngreso/getAllCombosRI";
import { adaptCombosRIArray } from "../../../../../../adapters/resumenIngreso/combosRIAdapter";
import { IndicacionesVisitaInput } from "./components/IndicacionesVisitaInput";
import { validateStepFields } from "../../../../utils/visitaStepValidation";
import { GET_SOLICITUD_INDICACION_PENDIENTE } from "../../../../../../data/graphQL/queries/GestionAsistencial/getSolicitudIndicacionPendiente";

export default function ProgramaCEAEMedico({ readOnly, onSaveRef, onExposeFormatted, complicacionesCombo, esUrgencia }) {
  const [formData, setFormData] = useState({});
  const [combosRI, setCombosRI] = useState();
  const { visita, setVisita } = useVisita();
  const [cambioIndicacion, setCambioIndicacion] = useState(false);
  const [solicitudIndicacionPendiente, setSolicitudIndicacionPendiente] = useState(null);

  const inputCombos = groups.flatMap((group) => group.inputs.filter((input) => !!input.comboName));
  const { data: dataCombosRI } = useQuery(getStringQueryCombosRI(inputCombos), {
    fetchPolicy: "no-cache"
  });

  useEffect(() => {
    if (!dataCombosRI) return;
    setCombosRI(adaptCombosRIArray(dataCombosRI))
  }, [dataCombosRI]);

  const [getSolicitudIndicacionPendiente, { data: dataSolicitudPendiente }] = useLazyQuery(GET_SOLICITUD_INDICACION_PENDIENTE, {
    fetchPolicy: "no-cache"
  });
  
  useEffect(() => {
    if (!dataSolicitudPendiente) return;
        setSolicitudIndicacionPendiente(dataSolicitudPendiente.solicitudIndicacionPendiente.success && dataSolicitudPendiente.solicitudIndicacionPendiente.valorEsperado!=null);
  }, [dataSolicitudPendiente]);

  const [save] = useMutation(SAVE_PROGRAMA_CEAE_MEDICO);

  useEffect(() => {
    const visitaIndicaciones = visita?.listaIndicacion || [];
    const formIndicaciones = formData?.listaIndicacion || [];

    if (visitaIndicaciones.length !== formIndicaciones.length) {
      setCambioIndicacion(true);
      return;
    }

    const formMap = new Map(
      formIndicaciones.map(ind => [ind.idInsumo, ind.cantidadDiaria])
    );

    const hayCambios = visitaIndicaciones.some(visitaInd => {
      const formCantidad = formMap.get(visitaInd.idInsumo);
      // Si no existe el insumo o la cantidad es diferente
      return formCantidad === undefined || formCantidad !== visitaInd.cantidadDiaria;
    });

    setCambioIndicacion(hayCambios);
  }, [visita?.listaIndicacion, formData?.listaIndicacion]);

  const formattedGroups = useMemo(() => {
    // IDs para SNG y Orogástrica desde combosRI.tipoAcceso
    const idComboRIValorTipoAcceso = formData?.idComboRIValorTipoAcceso;
    const sngId = combosRI?.idComboRIValorTipoAcceso?.find((c) => c.displayName.toLowerCase() === 'sng')?.id;
    const sogId = combosRI?.idComboRIValorTipoAcceso?.find((c) => c.displayName.toLowerCase() === 'orogastrica')?.id;
    const accEsSngUOrogastrica = [sngId, sogId].includes(idComboRIValorTipoAcceso);


    return groups.map((group) => {
      const formattedInputs = group.inputs.map((input) => {
        const newInput = { ...input };

        if (newInput.comboName) {
          newInput.options = combosRI?.[input.name];
        }

        switch (input.name) {
          // Accesos
          case "proteinasTotales":
            newInput.disabled = !!visita?.proteinasTotales;
            break;
          case "caloriasTotales":
            newInput.disabled = !!visita?.caloriasTotales;
            break;
          case "cambioSNG":
            newInput.hidden = !accEsSngUOrogastrica;
            // newInput.hidden = !esSngUOrogastrica;
            break;
          case "justificaCambio":
            newInput.hidden = !accEsSngUOrogastrica || formData?.cambioSNG !== false;
            newInput.required = accEsSngUOrogastrica && formData?.cambioSNG === false;
            break;
          case "justificaAccesoEnteral":
            newInput.hidden = formData?.accesoEnteral;
            newInput.required = !formData?.accesoEnteral;
            break;
          case "negativaGastrostomia":
            newInput.hidden = !accEsSngUOrogastrica;
            break;
          case "justificaGastrostomia":
            newInput.hidden = !accEsSngUOrogastrica || formData?.negativaGastrostomia !== true;
            newInput.required = accEsSngUOrogastrica && formData?.negativaGastrostomia === true;
            break;

          // Indicaciones
          case "justificacionModificacion":
            newInput.required = cambioIndicacion;
            break;
          case "flujoMlH":
            // Si la unidad de medida de la indicación es 'gr', este campo es OBL (si lo trae la grilla en formData.unidadMedida)
            newInput.required = formData?.unidadMedida === "gr";
            break;
          case "indicacionHidratacion":
            newInput.hidden = formData?.hidratacion !== true;
            newInput.required = formData?.hidratacion === true;
            break;
          case "suplemento":
          case "indicacionSuplemento":
            newInput.hidden = formData?.suplementoVitaminico !== true;
            newInput.required = formData?.suplementoVitaminico === true;
            break;

          // Requerimientos (condiciones de obligatoriedad)
          case "medicacionAnticonvulsivanteTexto":
            newInput.hidden = !(Number(formData?.cantidadMedicacionAnticonvulsivante) > 0);
            newInput.required = Number(formData?.cantidadMedicacionAnticonvulsivante) > 0;
            break;
          case "resultadoCetonemia":
            newInput.hidden = formData?.cetonemia !== true;
            newInput.required = formData?.cetonemia === true;
            break;
          case "resultadoCetonuria":
            newInput.hidden = formData?.cetonuria !== true;
            newInput.required = formData?.cetonuria === true;
            break;
          case "observacion":
            newInput.hidden = !(Number(formData?.cantidadCrisis) > 0);
            newInput.required = Number(formData?.cantidadCrisis) > 0;
            break;
          case "formaPreparacion":
            newInput.required = Array.isArray(formData?.listaIndicacion) && formData.listaIndicacion?.some((ind) => ind?.unidad === "grs");
            newInput.hidden = !newInput.required;
            break;

          case "complicaciones":
            const ningunaId = complicacionesCombo?.find((c) => c.displayName.toLowerCase() === 'ninguna')?.id;
            newInput.options = complicacionesCombo?.map((opt) => {
              if (Array.isArray(formData.complicaciones)) {
                const hasNinguna = formData.complicaciones.includes(ningunaId);
                const hasOther = formData.complicaciones.some((id) => id !== ningunaId);
                if (hasNinguna) {
                  // Si "ninguna" está seleccionada, deshabilitar todas menos "ninguna"
                  return { ...opt, disabled: opt.id !== ningunaId };
                }
                if (hasOther) {
                  // Si hay alguna distinta de "ninguna", deshabilitar "ninguna"
                  return { ...opt, disabled: opt.id === ningunaId };
                }
              }
              return { ...opt, disabled: false };
            });
            break;
          default:
            break;
        }

        if (readOnly) newInput.disabled = true;
        if (esUrgencia && input.name !== "justificacionModificacion") newInput.required = false;

        return newInput;
      });

      const hidden = !formattedInputs.some((i) => !i.hidden);
      return { ...group, hidden, inputs: formattedInputs };
    });
  }, [readOnly, formData, visita, combosRI, complicacionesCombo, cambioIndicacion, esUrgencia]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useStepSave(onSaveRef, async (validate) => {
    if (validate) validateStepFields({ formattedGroups, formData });
    const { data } = await save({ variables: { input: adaptAddVisitaCEAEMedicoInput(formData) } });

    const idVisitaCeae = data?.saveVisitaCeaeMedico?.idVisitaCeae;
    if (!idVisitaCeae) throw new Error("Error al guardar");

    setVisita((prev) => ({ ...prev, ...formData, idVisitaCeae }));
  });

  useEffect(() => {
    if (visita) {
      const inputNames = getInputNames(groups, true);

      setFormData({
        ...filterVisitaData(visita, inputNames),
        idVisitaCeae: visita.idVisitaCeae,
        idVisita: visita.idVisita
      });

    }
  }, [visita]);

  useEffect(() => {
    if (!visita?.idEvaluacion) return;
    
    getSolicitudIndicacionPendiente({ variables: { idEvaluacion: visita?.idEvaluacion } });
  }, [visita?.idEvaluacion]);

  const [proteinasTotales, caloriasTotales] = useMemo(() => {
    const indicaciones = Array.isArray(formData.listaIndicacion) ? formData.listaIndicacion : [];
    let totalProteinas = 0;
    let totalCalorias = 0;

    indicaciones.forEach((indicacion) => {
      const cantidad = Number(indicacion.cantidadDiaria) || 0;
      const proteinas = Number(indicacion.proteinas) || 0;
      const calorias = Number(indicacion.calorias) || 0;

      totalProteinas += cantidad * proteinas;
      totalCalorias += cantidad * calorias;
    });
    return [totalProteinas, totalCalorias];
  }, [formData.listaIndicacion]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      proteinasTotales,
      caloriasTotales
    }));
  }, [proteinasTotales, caloriasTotales]);

  useEffect(() => {
    onExposeFormatted?.({
      stepId: "programaCEAE",
      stepName: "Programa CEAE",
      formattedGroups
    });
  }, [onExposeFormatted, formattedGroups]);

  return (
    <>
      {formattedGroups.
        filter((group) => !group.hidden).
        map((group, idx) =>
          <CardBox key={idx} title={group.title}>
            <Grid container spacing={2}>
              {group.inputs.
                filter((input) => !input.hidden).
                map((input, idKey) =>
                  <Grid size={{ xs: 12 }} key={idKey + input.name}>
                    {input.name !== "listaIndicacion" ?
                      <RenderInputByTypeMobile
                        input={input}
                        value={formData[input.name]}
                        handleChange={handleChange}
                        row={input.type === "multiple_boolean"} /> :


                      <IndicacionesVisitaInput
                        readOnly={readOnly}
                        handleChange={handleChange}
                        indicaciones={formData[input.name]}
                        inputName={input.name}
                        idEvaluacion={visita?.idEvaluacion} 
                        solicitudIndicacionPendiente={solicitudIndicacionPendiente}
                        />
                    }
                  </Grid>
                )}
            </Grid>
          </CardBox>
        )}
    </>);

}

const groups = [
  {
    title: "",
    inputs: [
      {
        name: "ingestaOral",
        displayName: "Ingesta Oral",
        required: true,
        type: "boolean",
        lightTitle: true
      }]

  },
  {
    title: "Accesos",
    inputs: [
      {
        name: "idComboRIValorTipoAcceso",
        displayName: "Tipo de Acceso",
        comboName: "Tipo de Acceso",
        required: true,
        type: "select"
      },
      {
        name: "cambioSNG",
        displayName: "¿Cambio de SNG/SOG en domicilio?",
        required: true,
        type: "boolean",
        lightTitle: true
      },
      {
        name: "justificaCambio",
        displayName: "Si es NO, justificar",
        type: "long_string",
        maxLength: 500
      },
      {
        name: "accesoEnteral",
        displayName: "Acceso Enteral en buen estado",
        required: true,
        type: "boolean",
        lightTitle: true
      },
      {
        name: "justificaAccesoEnteral",
        displayName: "Si es NO, justificar",
        type: "long_string",
        maxLength: 500
      },
      {
        name: "negativaGastrostomia",
        displayName: "Negativa de gastrostomía",
        required: true,
        type: "boolean",
        lightTitle: true
      },
      {
        name: "justificaGastrostomia",
        displayName: "Si es SI, justificar",
        type: "long_string",
        maxLength: 4000
      }]

  },
  {
    title: "Indicaciones",
    inputs: [
      {
        name: "listaIndicacion",
        displayName: "Indicaciones (productos)",
        required: true,
        type: "custom"
      },
      {
        name: "justificacionModificacion",
        displayName: "Justificación por modificación",
        required: true,
        type: "long_string",
        maxLength: 4000
      },
      {
        name: "idComboRIValorModoInfusion",
        displayName: "Modo de infusión",
        comboName: "Modo de infusión NE",
        required: true,
        type: "select"
      },
      {
        name: "llevaBomba",
        displayName: "Lleva Bomba",
        required: true,
        type: "boolean",
        lightTitle: true
      },
      {
        name: "formaPreparacion",
        displayName: "Forma de preparación",
        required: true,
        type: "string",
        maxLength: 1000
      },
      {
        name: "indicacionEspecial",
        displayName: "Indicación especial",
        required: true,
        type: "long_string",
        maxLength: 1500
      },
      {
        name: "flujo",
        displayName: "Flujo (ml/h)",
        required: true,
        type: "int",
        min: 0
      },
      {
        name: "hidratacion",
        displayName: "Hidratación",
        required: true,
        type: "boolean",
        lightTitle: true
      },
      {
        name: "indicacionHidratacion",
        displayName: "Indicación de Hidratación",
        required: true,
        type: "long_string",
        maxLength: 1000
      },
      {
        name: "suplementoVitaminico",
        displayName: "Suplementos vitamínicos",
        required: true,
        type: "boolean",
        lightTitle: true
      },
      {
        name: "suplemento",
        displayName: "Suplementos",
        required: true,
        type: "string",
        maxLength: 1000
      },
      {
        name: "indicacionSuplemento",
        displayName: "Indicación de Suplementos",
        required: true,
        type: "long_string",
        maxLength: 1500
      }]

  },
  {
    title: "Requerimientos",
    inputs: [
      {
        name: "proteinasTotales",
        displayName: "Proteínas totales/d",
        required: true,
        type: "float",
        disabled: true // cálculo automático (mantenedor/grilla)
      },
      {
        name: "caloriasTotales",
        displayName: "Calorías totales/d",
        required: true,
        type: "float",
        disabled: true // cálculo automático (mantenedor/grilla)
      },
      {
        name: "cantidadAnticonvulsivante",
        displayName: "Cantidad medicación anticonvulsivante",
        required: true,
        type: "int",
        min: 0,
        max: 10
      },
      {
        name: "medicacionAnticonvulsivante",
        displayName: "Medicación anticonvulsivante",
        required: true,
        type: "long_string",
        maxLength: 1000
      },
      {
        name: "cetonemia",
        displayName: "Cetonemia",
        required: true,
        type: "boolean",
        lightTitle: true
      },
      {
        name: "resultadoCetonemia",
        displayName: "Resultado",
        required: true,
        type: "string",
        maxLength: 200
      },
      {
        name: "cetonuria",
        displayName: "Cetonuria",
        required: true,
        type: "boolean",
        lightTitle: true
      },
      {
        name: "resultadoCetonuria",
        displayName: "Resultado",
        required: true,
        type: "string",
        maxLength: 200
      },
      {
        name: "cantidadCrisis",
        displayName: "Cantidad de Crisis diarias",
        required: true,
        type: "int",
        min: 0,
        max: 100
      },
      {
        name: "observacion",
        displayName: "Observación",
        required: true,
        type: "long_string",
        maxLength: 1500
      },
      {
        name: "complicaciones",
        displayName: "Complicaciones",
        type: "multiple_select",
        required: true
      }]

  }];